import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const Watermark = ({ children, text = 'KRAYNE AGENCY', className = '', onClick = null }) => {
  const [showModal, setShowModal] = useState(false);
  
  // Gérer la touche Échap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    
    if (showModal) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showModal]);
  
  const buildWatermarkRows = (rowCount, repeatCount, modal = false) => {
    const watermarkPattern = Array.from({ length: repeatCount })
      .map(() => text)
      .join('   •   ');

    return Array.from({ length: rowCount }).map((_, index) => {
      const top = `${(index * 100) / (rowCount - 1 || 1)}%`;
      const offset = index % 2 === 0 ? '-18%' : '-8%';

      return (
        <div
          key={`${modal ? 'modal' : 'base'}-${index}`}
          className="absolute left-0 right-0 whitespace-nowrap select-none font-black uppercase"
          style={{
            top,
            left: offset,
            transform: 'translateY(-50%) rotate(-28deg)',
            color: modal ? 'rgba(255, 255, 255, 0.28)' : 'rgba(255, 255, 255, 0.22)',
            textShadow: modal
              ? '0 0 18px rgba(0, 0, 0, 0.95), 0 0 6px rgba(0, 0, 0, 0.65)'
              : '0 0 14px rgba(0, 0, 0, 0.85), 0 0 5px rgba(0, 0, 0, 0.55)',
            letterSpacing: modal ? '0.28em' : '0.24em',
            WebkitTextStroke: '0.4px rgba(255, 255, 255, 0.18)',
            fontSize: modal ? 'clamp(11px, 1vw, 18px)' : 'clamp(10px, 0.8vw, 16px)',
            lineHeight: 1,
            pointerEvents: 'none',
          }}
        >
          {watermarkPattern}
        </div>
      );
    });
  };

  const watermarks = buildWatermarkRows(9, 10, false);
  const modalWatermarks = buildWatermarkRows(11, 12, true);

  const handleImageClick = (e) => {
    // Vérifier si c'est une image
    if (e.target.tagName === 'IMG') {
      setShowModal(true);
    }
  };

  return (
    <>
      <div 
        className={`relative overflow-hidden group ${className}`}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          if (e.button === 2) e.preventDefault(); // Right click
        }}
        onClick={handleImageClick}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {/* Contenu enfant avec effet subtle au survol */}
        <div 
          className="relative h-full w-full select-none group-hover:brightness-105 transition-all duration-300"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          {children}
          {/* Overlay subtle pour plus de contraste */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/4 via-transparent to-black/4 pointer-events-none" />
        </div>
        
        {/* Grille dense de filigrames */}
        <div className="absolute inset-[-12%] pointer-events-none z-20 overflow-hidden">
          {watermarks}
        </div>

        {/* Overlay plus visible au survol */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/12 transition-colors duration-300 z-15 pointer-events-none" />
      </div>

      {/* Modal d'aperçu au clic */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermeture */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors z-10"
              title="Fermer (Échap)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Contenu modal - même contenu avec watermarks */}
            <div 
              className="relative w-full h-full max-h-[85vh] overflow-auto"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
            >
              {children && React.cloneElement(children, {
                style: { ...children.props.style, maxHeight: '85vh', objectFit: 'contain' }
              })}
              
              {/* Filigrames aussi dans le modal (très dense pour empêcher screenshot) */}
              <div className="absolute inset-[-10%] pointer-events-none z-20 overflow-hidden">
                {modalWatermarks}
              </div>
            </div>

            {/* Info au survol */}
            <p className="absolute bottom-4 left-4 text-white/60 text-sm pointer-events-none">
              Cliquez pour fermer • Protégé par filigrane
            </p>
          </div>
        </div>
      )}

      {/* Style pour désactiver le drag et drop et le menu contextuel */}
      <style>{`
        .group img {
          pointer-events: none;
          -webkit-user-drag: none;
          user-select: none;
          -webkit-user-select: none;
          display: block;
          width: 100%;
        }
        
        [oncontextmenu] {
          -webkit-touch-callout: none;
        }
      `}</style>
    </>
  );
};

export default Watermark;